import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import Login from "./Login";
import Home from "./Home";
import Profile from "./Profile";
import UserProfile from "./UserProfile";
import Friends from "./Friends";
import Revistas from "./Revistas";
import StartUps from "./StartUps";
import ResetPassword from "./ResetPassword";

// Componente principal da aplicação
function App() {
  // Estado que armazena o usuário autenticado (objeto do Supabase) ou null
  const [user, setUser] = useState(null);
  // Estado que indica se estamos aguardando a verificação do auth (tela de carregamento)
  const [loading, setLoading] = useState(true);
  // Controla se o modal de edição de perfil deve ser exibido
  const [showProfile, setShowProfile] = useState(false);
  // Guarda o id do usuário cujo perfil está sendo visualizado em um modal separado
  const [viewingUserId, setViewingUserId] = useState(null);
  // Controla se a lista de amigos (modal) deve ser exibida
  const [showFriends, setShowFriends] = useState(false);
  // Controla se a tela de StartUps deve ser exibida
  const [showStartUps, setShowStartUps] = useState(false);
  // Controla se a tela de Revistas deve ser exibida
  const [showRevistas, setShowRevistas] = useState(false);
  // Controla se a página de reset de senha deve ser exibida
  const [showResetPassword, setShowResetPassword] = useState(false);
  // Idioma do usuário
  const [userLanguage, setUserLanguage] = useState(() => {
    // Tentar carregar do localStorage primeiro
    return localStorage.getItem('preferredLanguage') || 'pt';
  });

  // Função para carregar o idioma do usuário do banco de dados
  const loadUserLanguage = async (userId) => {
    try {
      // Primeiro, verificar se há idioma salvo localmente
      const localLang = localStorage.getItem('preferredLanguage');
      
      // Buscar idioma do usuário no banco
            const userData = await userService.getUser();
      
      if (userData && userData.idioma) {
        setUserLanguage(userData.idioma);
        localStorage.setItem('preferredLanguage', userData.idioma);
        console.log('🌐 Idioma do usuário carregado do banco:', userData.idioma);
      } else if (localLang) {
        // Se não tem no banco mas tem local, usar local
        setUserLanguage(localLang);
        console.log('🌐 Idioma carregado do localStorage:', localLang);
      } else {
        // Default português
        setUserLanguage('pt');
        localStorage.setItem('preferredLanguage', 'pt');
      }
    } catch (error) {
      console.error('Erro ao carregar idioma:', error);
      const localLang = localStorage.getItem('preferredLanguage') || 'pt';
      setUserLanguage(localLang);
    }
  };

  useEffect(() => {
    // Verificar se está vindo da URL de reset de senha
    const hash = window.location.hash;
    if (hash && hash.includes('type=recovery')) {
      console.log('🔐 Detectado reset de senha via URL');
      setShowResetPassword(true);
    }

    // Listener para mudanças de autenticação do Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔔 Auth event:', event);
      
      // Se é evento de recuperação de senha
      if (event === 'PASSWORD_RECOVERY') {
        console.log('🔐 Evento PASSWORD_RECOVERY detectado');
        setShowResetPassword(true);
      }
      
      if (session?.user) {
        setUser(session.user);
      } else {
        setUser(null);
      }
      // Delay mínimo para garantir que a tela de loading seja visível
      setTimeout(() => setLoading(false), 800);
    });

    // Verificar se já existe uma sessão ativa
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
      }
      // Delay mínimo para garantir que a tela de loading seja visível
      setTimeout(() => setLoading(false), 800);
    };
    
    checkSession();

    // Listener para mensagens do UserProfile
    const handleMessage = (event) => {
      if (event.data.type === 'closeModal' || event.data.type === 'closeModalSilent') {
        // Fecha qualquer modal que esteja aberto
        setShowProfile(false);
        setViewingUserId(null);
        setShowFriends(false);
      }
    };

    window.addEventListener('message', handleMessage);

    // Limpa os listeners quando o componente desmontar
    return () => {
      subscription.unsubscribe();
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Manipulador de navegação interno usado por `Home` para abrir modais/telas
  // Recebe um código `p` que indica qual ação fazer e, opcionalmente, um userId
  function handleNav(p, userId = null) {
    console.log('📱 App.js - handleNav chamado:', p, userId);
    if (p === "profile") setShowProfile(true); // abrir modal de edição do próprio perfil
    if (p === "viewProfile") {
      // abrir modal de visualização de perfil de outro usuário
      console.log('👤 App.js - Definindo viewingUserId para:', userId);
      setViewingUserId(userId);
    }
    if (p === "friends") {
      // abrir modal de amigos
      setShowFriends(true);
    }
    if (p === "revistas") {
      // navegar para a tela de revistas
      setShowRevistas(true);
    }
    if (p === "startups") {
      // navegar para a tela de startups
      setShowStartUps(true);
    }
  }

  // Funções auxiliares para fechar modais/telas
  function closeProfile() {
    setShowProfile(false);
  }

  function closeUserProfile() {
    setViewingUserId(null);
  }

  function closeFriends() {
    setShowFriends(false);
  }

  function closeStartUps() {
    setShowStartUps(false);
  }

  function closeRevistas() {
    setShowRevistas(false);
  }

  // Renderização condicional baseada nos estados acima
  if (loading) {
    // Tela de carregamento exibida enquanto checamos o estado de autenticação
    return (
      <div className="orkut-loading-screen">
        <div className="orkut-loading-content">
          <h1 className="orkut-loading-title">toNetwork</h1>
          <div className="orkut-loading-text">Carregando...</div>
        </div>
      </div>
    );
  }

  // Se está na tela de reset de senha, mostrar apenas essa tela
  if (showResetPassword) {
    return <ResetPassword onComplete={() => setShowResetPassword(false)} />;
  }

  // Se não há usuário autenticado, mostra a tela de login
  if (!user) return <Login onLogin={setUser} />;

  // Se está na tela de StartUps, mostrar apenas essa tela
  if (showStartUps) {
    return <StartUps user={user} onNav={handleNav} onBack={closeStartUps} />;
  }

  // Se está na tela de Revistas, mostrar apenas essa tela
  if (showRevistas) {
    return <Revistas user={user} onNav={handleNav} onBack={closeRevistas} />;
  }

  // UI principal quando o usuário está autenticado
  return (
    <div>
      {/* Componente Home recebe o usuário e uma função onNav para navegar/abrir modais */}
      <Home user={user} onNav={handleNav} />

      {/* Modal de edição de perfil (próprio usuário) */}
      {showProfile && (
        <div className="orkut-modal-bg" onClick={closeProfile}>
          {/* stopPropagation evita que o clique no conteúdo feche o modal */}
          <div className="orkut-modal" onClick={(e) => e.stopPropagation()}>
            <div className="orkut-modal-header">
              <h2>Editar Perfil</h2>
              <button className="orkut-modal-close" onClick={closeProfile}>×</button>
            </div>
            <div className="orkut-modal-content">
              {/* Formulário/Componente de edição de perfil */}
              <Profile />
            </div>
          </div>
        </div>
      )}

      {/* Modal de visualização de perfil de outro usuário */}
      {viewingUserId && (
        <div className="orkut-modal-bg" onClick={closeUserProfile}>
          <div className="orkut-modal" onClick={(e) => e.stopPropagation()}>
            <div className="orkut-modal-header">
              <h2>Perfil</h2>
              <button className="orkut-modal-close" onClick={closeUserProfile}>×</button>
            </div>
            <div className="orkut-modal-content">
              {/* Passa o id do usuário para o componente que carrega o perfil */}
              <UserProfile userId={viewingUserId} />
            </div>
          </div>
        </div>
      )}

      {/* Modal com lista de amigos (tamanho maior) */}
      {showFriends && (
        <div className="orkut-modal-bg" onClick={closeFriends}>
          <div className="orkut-modal orkut-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="orkut-modal-header">
              <h2>Amigos</h2>
              <button className="orkut-modal-close" onClick={closeFriends}>×</button>
            </div>
            <div className="orkut-modal-content">
              {/* Componente que lista os amigos */}
              <Friends onNav={handleNav} onClose={closeFriends} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;